import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  patrocinadoresLocales,
  tiposPatrocinadorLocal,
  displaysPatrocinadorLocal,
  metricasGlobalesPatrocinadoresLocales,
  reglasSistemaPatrocinadoresLocales,
  metricasPorDisplay,
  getPatrocinadoresActivos,
  getTopPatrocinadoresPorInversion,
  getTopPatrocinadoresPorRendimiento,
  formatearDinero,
  formatearNumero,
  type TipoPatrocinadorLocal,
  type DisplayPatrocinadorLocal,
  type EstadoPatrocinio,
  type PatrocinadorLocal,
} from '@/mocks/patrocinadoresLocales';

const mg = metricasGlobalesPatrocinadoresLocales;

type TabId = 'todos' | 'negocios' | 'metricas' | 'reglas';
type SubTabId = DisplayPatrocinadorLocal | 'todos';

function TipoBadge({ tipo }: { tipo: TipoPatrocinadorLocal }) {
  const cfg = tiposPatrocinadorLocal.find(t => t.id === tipo);
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${cfg.bg} ${cfg.color}`}>
      <i className={`${cfg.icono} text-[9px]`}></i>
      {cfg.label}
    </span>
  );
}

function DisplayBadge({ display }: { display: string }) {
  const cfg = displaysPatrocinadorLocal.find(d => d.id === display);
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${cfg.bg} ${cfg.color}`}>
      <i className={`${cfg.icono} text-[7px]`}></i>
      {cfg.label}
    </span>
  );
}

function EstadoBadge({ estado }: { estado: EstadoPatrocinio }) {
  const styles: Record<EstadoPatrocinio, string> = {
    activo: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    pendiente: 'bg-amber-50 text-amber-600 border-amber-200',
    finalizado: 'bg-foreground-100 text-foreground-500 border-foreground-200',
    renovacion: 'bg-sky-50 text-sky-600 border-sky-200',
  };
  const labels: Record<EstadoPatrocinio, string> = {
    activo: 'Activo',
    pendiente: 'Pendiente',
    finalizado: 'Finalizado',
    renovacion: 'Renovación',
  };
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium border ${styles[estado]}`}>
      {labels[estado]}
    </span>
  );
}

function SponsorDetailModal({ sponsor, onClose }: { sponsor: PatrocinadorLocal; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-lg bg-background-50 rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-500 transition-colors z-10 cursor-pointer">
          <i className="ri-close-line"></i>
        </button>

        <div className="h-40 bg-gradient-to-br from-emerald-100 via-teal-50 to-amber-50 flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
          <img src={sponsor.imagenUrl} alt={sponsor.nombreNegocio} className="w-full h-full object-cover object-top" loading="lazy" />
          <div className="absolute -bottom-6 left-5">
            <div className="w-16 h-16 rounded-xl overflow-hidden bg-white p-1.5 shadow-lg">
              <img src={sponsor.logoUrl} alt={sponsor.nombreNegocio} className="w-full h-full object-cover rounded-lg" />
            </div>
          </div>
        </div>

        <div className="p-5 pt-8">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-foreground-950">{sponsor.nombreNegocio}</h3>
            <TipoBadge tipo={sponsor.tipo} />
          </div>

          <p className="text-xs text-foreground-600 mb-3 leading-relaxed">{sponsor.descripcion}</p>

          {/* Métricas grid */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg bg-background-100 p-3 text-center">
              <p className="text-lg font-extrabold text-emerald-600">{sponsor.metricas.ctr > 0 ? `${sponsor.metricas.ctr}%` : '—'}</p>
              <p className="text-[9px] text-foreground-500">CTR</p>
            </div>
            <div className="rounded-lg bg-background-100 p-3 text-center">
              <p className="text-lg font-extrabold text-amber-600">{sponsor.metricas.conversionRate > 0 ? `${sponsor.metricas.conversionRate}%` : '—'}</p>
              <p className="text-[9px] text-foreground-500">Conversión</p>
            </div>
            <div className="rounded-lg bg-background-100 p-3 text-center">
              <p className="text-lg font-extrabold text-foreground-950">{sponsor.metricas.ctr > 0 ? formatearNumero(sponsor.metricas.impresiones) : '—'}</p>
              <p className="text-[9px] text-foreground-500">Impresiones</p>
            </div>
          </div>

          {/* Metadata grid */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[9px] text-foreground-500">Radio</p>
              <div className="flex items-center gap-2 mt-0.5">
                <img src={sponsor.radioLogoUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
                <p className="text-xs font-semibold text-foreground-950">{sponsor.radioNombre} {sponsor.radioFrecuencia}</p>
              </div>
            </div>
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[9px] text-foreground-500">Inversión</p>
              <p className="text-sm font-extrabold text-emerald-600">{formatearDinero(sponsor.inversionTotal)}</p>
              <p className="text-[9px] text-foreground-400">{formatearDinero(sponsor.inversionMensual)}/mes</p>
            </div>
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[9px] text-foreground-500">Ubicación</p>
              <p className="text-xs font-semibold text-foreground-950">{sponsor.ciudad}, {sponsor.pais}</p>
            </div>
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[9px] text-foreground-500">Contacto</p>
              <p className="text-xs font-semibold text-foreground-950">{sponsor.contactoNombre}</p>
              <p className="text-[9px] text-foreground-500">{sponsor.contactoEmail}</p>
            </div>
          </div>

          {/* Display */}
          <div className="mb-3">
            <p className="text-[10px] font-semibold text-foreground-500 mb-1.5">Visible en</p>
            <div className="flex flex-wrap gap-1">
              {sponsor.displayEn.map(d => <DisplayBadge key={d} display={d} />)}
            </div>
          </div>

          {/* Beneficios */}
          {sponsor.beneficios.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] font-semibold text-foreground-500 mb-1.5">Beneficios</p>
              <div className="space-y-1">
                {sponsor.beneficios.map((b, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs text-foreground-700">
                    <i className="ri-check-line text-emerald-500 text-[10px]"></i>
                    {b}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-1">
            {sponsor.tags.map(tag => (
              <span key={tag} className="px-1.5 py-0.5 rounded-md bg-background-100 text-[9px] text-foreground-500">{tag}</span>
            ))}
          </div>

          {/* Contact links */}
          <div className="flex items-center gap-3 mt-4 pt-3 border-t border-background-200/70 text-[10px]">
            <a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700 transition-colors">
              <i className="ri-global-line"></i> Sitio Web
            </a>
            <a href={`https://wa.me/${sponsor.contactoTelefono.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-amber-600 hover:text-amber-700 transition-colors">
              <i className="ri-whatsapp-line"></i> WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PatrocinadoresLocalesPage() {
  const [activeTab, setActiveTab] = useState<TabId>('todos');
  const [tipoFiltro, setTipoFiltro] = useState<TipoPatrocinadorLocal | 'todos'>('todos');
  const [displayFiltro, setDisplayFiltro] = useState<SubTabId>('todos');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoPatrocinio | 'todos'>('todos');
  const [ordenamiento, setOrdenamiento] = useState<'inversion' | 'ctr' | 'recientes' | 'nombre'>('inversion');
  const [selectedSponsor, setSelectedSponsor] = useState<PatrocinadorLocal | null>(null);

  const filtrados = (() => {
    let list = patrocinadoresLocales;
    if (tipoFiltro !== 'todos') list = list.filter(p => p.tipo === tipoFiltro);
    if (displayFiltro !== 'todos') list = list.filter(p => p.displayEn.includes(displayFiltro));
    if (estadoFiltro !== 'todos') list = list.filter(p => p.estado === estadoFiltro);

    switch (ordenamiento) {
      case 'inversion': list = [...list].sort((a, b) => b.inversionTotal - a.inversionTotal); break;
      case 'ctr': list = [...list].sort((a, b) => b.metricas.ctr - a.metricas.ctr); break;
      case 'recientes': list = [...list].sort((a, b) => new Date(b.fechaRegistro).getTime() - new Date(a.fechaRegistro).getTime()); break;
      case 'nombre': list = [...list].sort((a, b) => a.nombreNegocio.localeCompare(b.nombreNegocio)); break;
    }
    return list;
  })();

  const tabs: { id: TabId; label: string; icono: string; count?: number }[] = [
    { id: 'todos', label: 'Todos', icono: 'ri-layout-grid-line', count: patrocinadoresLocales.length },
    { id: 'negocios', label: 'Por Tipo', icono: 'ri-store-2-line' },
    { id: 'metricas', label: 'Métricas', icono: 'ri-bar-chart-line' },
    { id: 'reglas', label: 'Reglas', icono: 'ri-scales-line', count: reglasSistemaPatrocinadoresLocales.length },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-emerald-50 via-background-50 to-amber-50">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-72 h-72 bg-emerald-400/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-amber-400/8 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-emerald-200 text-emerald-700 text-xs font-semibold mb-6 shadow-sm">
            <i className="ri-store-2-line text-xs"></i>
            BLOQUE 64 · PATROCINADORES LOCALES
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground-950 tracking-tight leading-[1.1] mb-4">
            Negocios reales,{' '}
            <span className="text-emerald-600">radios locales</span>
          </h1>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-8">
            La radio administra sus propios patrocinadores. Negocios de barrio, restaurantes, tiendas, servicios, empresas y eventos patrocinando radios de su zona. Métricas transparentes, gestión independiente.
          </p>

          {/* Hero stats */}
          <div className="inline-flex flex-wrap items-center justify-center gap-4 md:gap-8">
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-extrabold text-emerald-600 tabular-nums">{mg.totalPatrocinadores}</p>
              <p className="text-[10px] text-foreground-500 font-medium">Patrocinadores Locales</p>
            </div>
            <div className="w-px h-8 bg-emerald-200 hidden sm:block"></div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-extrabold text-amber-600 tabular-nums">{mg.patrocinadoresActivos}</p>
              <p className="text-[10px] text-foreground-500 font-medium">Activos</p>
            </div>
            <div className="w-px h-8 bg-emerald-200 hidden sm:block"></div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-extrabold text-foreground-950 tabular-nums">{formatearDinero(mg.inversionTotal)}</p>
              <p className="text-[10px] text-foreground-500 font-medium">Inversión Total</p>
            </div>
            <div className="w-px h-8 bg-emerald-200 hidden sm:block"></div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-extrabold text-emerald-600 tabular-nums">{mg.ctrPromedio}%</p>
              <p className="text-[10px] text-foreground-500 font-medium">CTR Promedio</p>
            </div>
            <div className="w-px h-8 bg-emerald-200 hidden sm:block"></div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-extrabold text-foreground-950 tabular-nums">{formatearNumero(mg.leadsTotales)}</p>
              <p className="text-[10px] text-foreground-500 font-medium">Leads Generados</p>
            </div>
          </div>
        </div>
      </section>

      {/* Radio Administra Banner */}
      <section className="bg-emerald-600">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-radio-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm md:text-base">La Radio Administra sus Propios Patrocinadores.</p>
              <p className="text-emerald-100 text-xs">Negociación, venta, gestión y cobro son 100% de la radio. RFW muestra las métricas.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-white/80 text-xs">
            <span className="flex items-center gap-1"><i className="ri-bank-line"></i> Ganancia 100% Radio</span>
            <span className="hidden sm:block">·</span>
            <span className="flex items-center gap-1 hidden sm:flex"><i className="ri-bar-chart-line"></i> Métricas Transparentes</span>
          </div>
        </div>
      </section>

      {/* Sticky Tabs */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="flex items-center gap-1 py-3 overflow-x-auto scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                }`}
              >
                <i className={`${tab.icono} text-[10px]`}></i>
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'
                  }`}>{tab.count}</span>
                )}
              </button>
            ))}

            {/* Back link */}
            <div className="ml-auto flex items-center gap-2">
              <Link
                to="/"
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium text-foreground-500 hover:text-foreground-900 hover:bg-background-100 transition-all whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-[10px]"></i>
                RFW
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto py-10">
        {/* Todos / Negocios Tab */}
        {(activeTab === 'todos' || activeTab === 'negocios') && (
          <div>
            {/* Filters row */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              {/* Tipo filter */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 mr-1">Tipo:</span>
                {[{ id: 'todos' as const, label: 'Todos' }, ...tiposPatrocinadorLocal.map(t => ({ id: t.id, label: t.label }))].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setTipoFiltro(f.id)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      tipoFiltro === f.id
                        ? 'bg-emerald-500 text-white shadow-sm'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-900 border border-background-200/70'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Display filter */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 mr-1">Display:</span>
                {[{ id: 'todos' as const, label: 'Todos' }, ...displaysPatrocinadorLocal.map(d => ({ id: d.id, label: d.label }))].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setDisplayFiltro(f.id)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      displayFiltro === f.id
                        ? 'bg-amber-500 text-white shadow-sm'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-900 border border-background-200/70'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Estado filter */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 mr-1">Estado:</span>
                {(['todos' as const, 'activo' as const, 'pendiente' as const, 'finalizado' as const]).map(s => (
                  <button
                    key={s}
                    onClick={() => setEstadoFiltro(s)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-medium capitalize transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      estadoFiltro === s
                        ? s === 'activo' ? 'bg-emerald-500 text-white shadow-sm' : s === 'pendiente' ? 'bg-amber-500 text-white shadow-sm' : s === 'finalizado' ? 'bg-foreground-500 text-white shadow-sm' : 'bg-emerald-500 text-white shadow-sm'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-900 border border-background-200/70'
                    }`}
                  >
                    {s === 'todos' ? 'Todos' : s}
                  </button>
                ))}
              </div>
            </div>

            {/* Ordenamiento */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-foreground-500 mr-1">Ordenar:</span>
              {([
                { id: 'inversion' as const, label: 'Mayor Inversión', icono: 'ri-money-dollar-circle-line' },
                { id: 'ctr' as const, label: 'Mejor CTR', icono: 'ri-line-chart-line' },
                { id: 'recientes' as const, label: 'Más Recientes', icono: 'ri-time-line' },
                { id: 'nombre' as const, label: 'A-Z', icono: 'ri-sort-alphabet-asc' },
              ]).map(o => (
                <button
                  key={o.id}
                  onClick={() => setOrdenamiento(o.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    ordenamiento === o.id
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900 border border-background-200/70'
                  }`}
                >
                  <i className={`${o.icono} text-[10px]`}></i>
                  {o.label}
                </button>
              ))}
            </div>

            <p className="text-xs text-foreground-500 mb-4">{filtrados.length} patrocinadores</p>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtrados.map(p => (
                <div
                  key={p.id}
                  onClick={() => setSelectedSponsor(p)}
                  className="rounded-xl bg-background-50 border border-background-200/70 p-4 hover:border-emerald-300/50 hover:shadow-md transition-all duration-200 cursor-pointer group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={p.logoUrl} alt={p.nombreNegocio} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-bold text-foreground-950 truncate">{p.nombreNegocio}</h4>
                      <p className="text-[10px] text-foreground-500">{p.sector} · {p.ciudad}</p>
                    </div>
                  </div>

                  <TipoBadge tipo={p.tipo} />
                  <p className="text-xs text-foreground-600 mt-2 line-clamp-2 leading-relaxed">{p.descripcion}</p>

                  {/* Radio */}
                  <div className="mt-2 flex items-center gap-2 text-[10px] text-foreground-500">
                    <img src={p.radioLogoUrl} alt="" className="w-4 h-4 rounded-full object-cover" />
                    <span>{p.radioNombre} {p.radioFrecuencia}</span>
                  </div>

                  {/* Display badges */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.displayEn.map(d => <DisplayBadge key={d} display={d} />)}
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {p.tags.slice(0, 2).map(tag => (
                      <span key={tag} className="px-1.5 py-0.5 rounded-md bg-background-100 text-[9px] text-foreground-500">{tag}</span>
                    ))}
                  </div>

                  {/* Métricas */}
                  {p.metricas.ctr > 0 && (
                    <div className="mt-3 pt-3 border-t border-background-200/70 grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-[9px] text-foreground-400">Impresiones</p>
                        <p className="text-xs font-bold text-foreground-800">{formatearNumero(p.metricas.impresiones)}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-foreground-400">CTR / Conv.</p>
                        <p className="text-xs font-bold text-emerald-600">{p.metricas.ctr}% / {p.metricas.conversionRate}%</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-foreground-400">Clicks</p>
                        <p className="text-xs font-bold text-foreground-800">{formatearNumero(p.metricas.clicks)}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-foreground-400">Leads</p>
                        <p className="text-xs font-bold text-amber-600">{formatearNumero(p.metricas.leads)}</p>
                      </div>
                    </div>
                  )}

                  {/* Investment + Estado */}
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm font-extrabold text-emerald-600">{formatearDinero(p.inversionTotal)}</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-foreground-400">{formatearDinero(p.inversionMensual)}/mes</span>
                      <EstadoBadge estado={p.estado} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty state */}
            {filtrados.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                  <i className="ri-search-line text-foreground-400 text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-950 mb-1">Sin resultados</h3>
                <p className="text-sm text-foreground-500 mb-4">No hay patrocinadores con los filtros actuales</p>
                <button
                  onClick={() => { setTipoFiltro('todos'); setDisplayFiltro('todos'); setEstadoFiltro('todos'); }}
                  className="px-4 py-2 rounded-xl bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
                >
                  Limpiar filtros
                </button>
              </div>
            )}
          </div>
        )}

        {/* Métricas Tab */}
        {activeTab === 'metricas' && (
          <div className="space-y-8">
            {/* KPI Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <i className="ri-hand-heart-line text-emerald-600"></i>
                </div>
                <p className="text-2xl font-extrabold text-emerald-600 tabular-nums">{mg.totalPatrocinadores}</p>
                <p className="text-xs text-foreground-500 mt-1">Patrocinadores</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-amber-100 flex items-center justify-center">
                  <i className="ri-flashlight-line text-amber-600"></i>
                </div>
                <p className="text-2xl font-extrabold text-amber-600 tabular-nums">{mg.patrocinadoresActivos}</p>
                <p className="text-xs text-foreground-500 mt-1">Activos</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-emerald-600"></i>
                </div>
                <p className="text-2xl font-extrabold text-foreground-950 tabular-nums">{formatearDinero(mg.inversionTotal)}</p>
                <p className="text-xs text-foreground-500 mt-1">Inversión Total</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-rose-100 flex items-center justify-center">
                  <i className="ri-eye-line text-rose-600"></i>
                </div>
                <p className="text-2xl font-extrabold text-foreground-950 tabular-nums">{formatearNumero(mg.impresionesTotales)}</p>
                <p className="text-xs text-foreground-500 mt-1">Impresiones</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-purple-100 flex items-center justify-center">
                  <i className="ri-line-chart-line text-purple-600"></i>
                </div>
                <p className="text-2xl font-extrabold text-emerald-600 tabular-nums">{mg.ctrPromedio}%</p>
                <p className="text-xs text-foreground-500 mt-1">CTR Promedio</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-sky-100 flex items-center justify-center">
                  <i className="ri-user-add-line text-sky-600"></i>
                </div>
                <p className="text-2xl font-extrabold text-foreground-950 tabular-nums">{formatearNumero(mg.leadsTotales)}</p>
                <p className="text-xs text-foreground-500 mt-1">Leads</p>
              </div>
            </div>

            {/* Second row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-cursor-line text-emerald-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-lg font-extrabold text-foreground-950 tabular-nums">{formatearNumero(mg.clicksTotales)}</p>
                  <p className="text-[10px] text-foreground-500">Clicks Totales</p>
                </div>
              </div>
              <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-radar-line text-amber-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-lg font-extrabold text-foreground-950 tabular-nums">{formatearNumero(mg.alcanceTotal)}</p>
                  <p className="text-[10px] text-foreground-500">Alcance Total</p>
                </div>
              </div>
              <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-double-line text-emerald-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-lg font-extrabold text-foreground-950 tabular-nums">{mg.conversionPromedio}%</p>
                  <p className="text-[10px] text-foreground-500">Conversión Promedio</p>
                </div>
              </div>
              <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-radio-line text-emerald-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-lg font-extrabold text-foreground-950 tabular-nums">{mg.radiosConPatrocinio}</p>
                  <p className="text-[10px] text-foreground-500">Radios con Patrocinio</p>
                </div>
              </div>
            </div>

            {/* Display breakdown */}
            <div className="rounded-2xl bg-background-100 border border-background-200/70 p-6">
              <h3 className="text-sm font-bold text-foreground-950 mb-4">Métricas por Ubicación de Display</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {metricasPorDisplay.map(md => (
                  <div key={md.display} className="rounded-xl bg-background-50 p-4 text-center border border-background-200/70">
                    <div className={`w-10 h-10 mx-auto mb-2 rounded-xl ${displaysPatrocinadorLocal.find(d => d.id === md.display)?.bg || 'bg-emerald-50'} flex items-center justify-center`}>
                      <i className={`${md.icono} text-lg ${displaysPatrocinadorLocal.find(d => d.id === md.display)?.color || 'text-emerald-600'}`}></i>
                    </div>
                    <p className="text-xs font-bold text-foreground-950">{md.label}</p>
                    <p className="text-lg font-extrabold text-emerald-600 tabular-nums mt-1">{md.totalPatrocinios}</p>
                    <p className="text-[9px] text-foreground-500">patrocinios</p>
                    <p className="text-xs font-semibold text-foreground-800 mt-1">{formatearDinero(md.inversionTotal)}</p>
                    <p className="text-[9px] text-foreground-400">{formatearNumero(md.impresionesTotales)} impr.</p>
                    <p className="text-[9px] text-foreground-400">{formatearNumero(md.clicksTotales)} clicks</p>
                    <p className="text-[9px] text-foreground-400">{formatearNumero(md.leadsTotales)} leads</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Reglas Tab */}
        {activeTab === 'reglas' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reglasSistemaPatrocinadoresLocales.map(regla => (
                <div key={regla.id} className={`rounded-2xl bg-gradient-to-br ${regla.gradiente} border ${regla.borde} p-6 hover:shadow-md transition-all duration-200 cursor-pointer`}>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-white/60 flex items-center justify-center flex-shrink-0">
                      <i className={`${regla.icono} ${regla.colorTexto} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-foreground-950">{regla.titulo}</h3>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{regla.descripcion}</p>
                </div>
              ))}
            </div>

            {/* Stats summary */}
            <div className="rounded-2xl bg-background-100 border border-background-200/70 p-6">
              <h3 className="text-sm font-bold text-foreground-950 mb-4">Resumen del Sistema</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-emerald-600 tabular-nums">{mg.totalPatrocinadores}</p>
                  <p className="text-[10px] text-foreground-500">Patrocinadores</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-amber-600 tabular-nums">6</p>
                  <p className="text-[10px] text-foreground-500">Tipos de Negocio</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-violet-600 tabular-nums">5</p>
                  <p className="text-[10px] text-foreground-500">Ubicaciones Display</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-foreground-950 tabular-nums">{mg.radiosConPatrocinio}</p>
                  <p className="text-[10px] text-foreground-500">Radios con Patrocinio</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-emerald-600 tabular-nums">100%</p>
                  <p className="text-[10px] text-foreground-500">Ganancia Radio</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA links */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <Link to="/patrocinadores" className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50 border border-background-200/70 hover:border-emerald-300 text-foreground-800 text-xs font-semibold rounded-xl transition-all duration-200 whitespace-nowrap">
            <i className="ri-hand-heart-line"></i>
            Patrocinadores de Concursos
          </Link>
          <Link to="/publicidad" className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50 border border-background-200/70 hover:border-emerald-300 text-foreground-800 text-xs font-semibold rounded-xl transition-all duration-200 whitespace-nowrap">
            <i className="ri-megaphone-line"></i>
            Publicidad General
          </Link>
          <Link to="/tv-app" className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-xl transition-all duration-200 whitespace-nowrap">
            <i className="ri-tv-line"></i>
            Ver en TV App
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>

      {/* Bottom CTA - Rule */}
      <section className="bg-background-100 border-t border-background-200/70 py-12">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-4">
            <i className="ri-check-double-line text-xs"></i>
            REGLA FUNDAMENTAL
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            La radio administra sus propios patrocinadores.
          </h2>
          <p className="text-foreground-600 text-sm max-w-xl mx-auto mb-6">
            RFW no interviene en la venta. Negociación, cobro y gestión son 100% de la radio. Las métricas son transparentes para que el patrocinador vea resultados, pero cada radio maneja sus patrocinadores de forma independiente.
          </p>
          <div className="flex items-center justify-center gap-3 text-foreground-500 text-xs">
            <span className="flex items-center gap-1"><i className="ri-radio-line text-emerald-500"></i> Gestión Independiente</span>
            <span>·</span>
            <span className="flex items-center gap-1"><i className="ri-bar-chart-line text-emerald-500"></i> Métricas Transparentes</span>
            <span>·</span>
            <span className="flex items-center gap-1"><i className="ri-bank-line text-emerald-500"></i> 100% Radio</span>
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedSponsor && (
        <SponsorDetailModal sponsor={selectedSponsor} onClose={() => setSelectedSponsor(null)} />
      )}
    </div>
  );
}